import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStripe } from "@stripe/react-stripe-js";
import { useQueryClient } from "@tanstack/react-query";
import { getMyExplorerOnboarding } from "../../api/explorerOnboardingApi";
import { useBookEvent } from "../../api/mutations/useBookEvent";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useGetEvent } from "../../api/queries/useGetEvent";
import { useSaveUser } from "../../api/mutations/useSaveUser";
import axiosInstance, { getAPIURL } from "../../api/axiosInstance";
import {
  continueEarlyStageAtStandardRate,
  getEarlyStageStatus,
  type EarlyStageVerificationStatus,
} from "../../api/earlyStageMembershipApi";
import { useSendEventBookingConfirmation } from "../../api/mutations/useSendEventBookingConfirmation";
import { useAppContext } from "../../Context/AppContext";
import { resolveEventBookingFormData } from "../../explorer/resolveEventBookingFormData";
import { useCurrentMembership } from "../../hooks/useCurrentMembership";
import { isPaidPortalMember } from "../../utils/membershipTier";
import {
  EXPLORER_EVENT_PAYMENT_DONE_KEY,
  clearExplorerPostMembershipEventCombo,
  getExplorerPostMembershipEventCombo,
  getExplorerFlow,
  getExplorerLocalProfile,
  notifyExplorerComboRegisterChromeChanged,
} from "../../explorer/explorerSession";
import { splitCityCountry } from "../../utils/cityAsyncSelectOptions";
import { Event } from "../../types/Types";
import { eventEligibleForMemberTierDiscount } from "../../utils/eventMemberDiscount";
import { isEventFreeForGroup } from "../../utils/eventFreeForGroups";
import "../css/RegisterForm.css";
import {
  buildRegistrationFormDataAfterPayment,
  clearRegistrationPaidMembershipPrice,
  readRegistrationPaidMembershipPrice,
} from "./registrationCheckoutShared";
import CheckoutStep from "./RegistrationsSteps/CheckoutStep";
import ConfirmationStep from "./RegistrationsSteps/ConfirmationStep";
import MembershipStep from "./RegistrationsSteps/MemberShipStep";
import PersonalInfoStep, {
  type PersonalInfoData,
} from "./RegistrationsSteps/PersonalInfoStep";
import TermsStep from "./RegistrationsSteps/TermsStep";
import StepsProgressBar from "./StepsProgressBar";
import RegistrationEarlyStageAgeStep from "./RegistrationEarlyStageAgeStep";
import EarlyStagePlanStep from "./EarlyStagePlanStep";
import {
  findMembershipForCheckout,
  formSaveIndicatesPaymentComplete,
  resolveMembershipIdForName,
} from "./registrationMembershipSync";
import {
  resolveEarlyStageRegistrationStep,
  resolveStandardRegistrationStep,
  needsEarlyStagePricePlanSelection,
  hasRegistrationPersonalInfoSaved,
  type RegistrationUserSnapshot,
  isEarlyStageVerificationInProgress,
  earlyStagePendingProceedingAtStandard,
  isEarlyStageStandardTenPoundPathCommitted,
} from "./registrationResume";
import {
  EARLY_STAGE_MEMBERSHIP,
  isEarlyStageMembership,
  sortMembershipsForRegistration,
} from "../../utils/membershipTier";
import { isEarlyStageRejectionNoticeVisible } from "../../utils/earlyStageRejectionNotice";
import EarlyStageRegistrationNotice from "./EarlyStageRegistrationNotice";
import EarlyStagePendingStep from "./EarlyStagePendingStep";
import EarlyStageApprovedStep from "./EarlyStageApprovedStep";
import EarlyStageRejectedNotice from "./EarlyStageRejectedNotice";
import { Spinner } from "react-bootstrap";

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  city: string;
  country: string;
  personalPositionCompany: string;
  businessName: string;
  isEmailUpdates: number;
  isGDPRConsent: number;
  membershipName: string;
  isAnnualMembership: number;
  isTermConditions: number;
  isDirectory: number;
  billingAddress: string;
  paymentMethodId: string;
  referralCodeUsed?: string;
}

const REGISTER_MEMBERSHIP_QUERY = "membership";

const ALLOWED_URL_MEMBERSHIPS = [
  "Business Membership",
  "Executive Membership",
  "Youth Membership",
  "Early Stage Membership",
] as const;

const MEMBERSHIP_BUSINESS = "Business Membership";
const MEMBERSHIP_EXECUTIVE = "Executive Membership";

const PERSONAL_PREFILL_KEYS: (keyof FormData)[] = [
  "firstName",
  "lastName",
  "email",
  "phoneNumber",
  "city",
  "country",
  "personalPositionCompany",
  "businessName",
  "billingAddress",
];

function strVal(v: unknown): string {
  return typeof v === "string" ? v.trim() : "";
}

/** Map Explorer API row (camelCase or PascalCase) into registration form fields. */
function explorerToFormPartial(ex: unknown): Partial<FormData> {
  if (!ex || typeof ex !== "object") return {};
  const o = ex as Record<string, unknown>;
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const t = strVal(o[k]);
      if (t) return t;
    }
    return "";
  };
  const { city, country } = splitCityCountry(pick("city", "City"), "");
  return {
    firstName: pick("firstName", "FirstName"),
    lastName: pick("lastName", "LastName"),
    email: pick("email", "Email"),
    phoneNumber: pick("phoneNumber", "PhoneNumber"),
    city,
    country,
    personalPositionCompany: pick(
      "personalPositionCompany",
      "jobTitle",
      "JobTitle",
    ),
    businessName: pick("businessName", "companyName", "CompanyName"),
  };
}

/** Explorer step-1 session (separate city/country, same as PersonalInfoStep). */
function explorerSessionToFormPartial(): Partial<FormData> {
  const local = getExplorerLocalProfile();
  if (!local) return {};
  const { city, country } = splitCityCountry(local.city, local.country);
  return {
    firstName: strVal(local.firstName),
    lastName: strVal(local.lastName),
    email: strVal(local.email),
    phoneNumber: strVal(local.phoneNumber),
    city,
    country,
    personalPositionCompany: strVal(local.jobTitle),
    businessName: strVal(local.companyName),
  };
}

function readUserDataFromStorage(): Partial<FormData> {
  try {
    const raw = localStorage.getItem("UserData");
    if (!raw) return {};
    const p = JSON.parse(raw) as Record<string, unknown>;
    const { city, country } = splitCityCountry(
      strVal(p.city),
      strVal(p.country),
    );
    return {
      firstName: strVal(p.firstName),
      lastName: strVal(p.lastName),
      email: strVal(p.email),
      phoneNumber: strVal(p.phoneNumber),
      city,
      country,
      personalPositionCompany: strVal(p.personalPositionCompany),
      businessName: strVal(p.businessName),
      billingAddress: strVal(p.billingAddress),
      membershipName: strVal(p.membershipName),
    };
  } catch {
    return {};
  }
}

/** Fill only empty fields; first matching layer wins (portal → explorer → saved draft → auth email). */
function mergeEmptyPersonalFields(
  prev: FormData,
  layers: Partial<FormData>[],
): FormData {
  let next = { ...prev };
  for (const key of PERSONAL_PREFILL_KEYS) {
    if (strVal(next[key])) continue;
    for (const layer of layers) {
      const t = strVal(layer[key]);
      if (t) {
        next = { ...next, [key]: t };
        break;
      }
    }
  }
  const { city, country } = splitCityCountry(next.city, next.country);
  return { ...next, city, country };
}

/** Hydrate from UserData + auth_email on first paint so we do not overwrite signup names with empty state. */
function getInitialFormDataFromStorage(): FormData {
  const stored = readUserDataFromStorage();
  let authEmail = "";
  try {
    authEmail = localStorage.getItem("auth_email")?.trim() ?? "";
  } catch {
    /* ignore */
  }
  return {
    firstName: stored.firstName || "",
    lastName: stored.lastName || "",
    email: stored.email || authEmail,
    phoneNumber: stored.phoneNumber || "",
    city: stored.city || "",
    country: stored.country || "",
    personalPositionCompany: stored.personalPositionCompany || "",
    businessName: stored.businessName || "",
    isEmailUpdates: 0,
    isGDPRConsent: 0,
    membershipName: stored.membershipName || "",
    isAnnualMembership: 0,
    isTermConditions: 0,
    isDirectory: 0,
    billingAddress: stored.billingAddress || "",
    paymentMethodId: "",
  };
}

const RegisterUser = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { data: memberships } = useGetMemberships();
  const [membershipPrice, setMembershipPrice] = useState<number>(0);
  const [step, setStep] = useState(0);
  const [userData, setUserData] = useState<Record<string, any>>({});
  const [discountCode, setDiscountCode] = useState("");
  const [formData, setFormData] = useState<FormData>(
    getInitialFormDataFromStorage,
  );
  const [earlyStageResumeStatus, setEarlyStageResumeStatus] =
    useState<EarlyStageVerificationStatus | null>(null);
  const [registrationResuming, setRegistrationResuming] = useState(false);
  /** True only after Early Stage is saved on the user row (membership step or resume). */
  const [earlyStagePath, setEarlyStagePath] = useState(false);
  /** True when user chose the under-23 Young Professional path from membership step. */
  const [earlyStageYoungProfessionalPath, setEarlyStageYoungProfessionalPath] =
    useState(false);
  /** True after user proceeds on the standard £10 Early Stage path (hide under-23 link on return). */
  const [earlyStageStandardTenCommitted, setEarlyStageStandardTenCommitted] =
    useState(false);
  const formDataRef = useRef(formData);
  const userHasNavigatedRef = useRef(false);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const { objectId, user } = useAppContext();
  const queryClient = useQueryClient();
  const stripe = useStripe();
  const navigate = useNavigate();

  const { isExplorer: skipPersonalInfoStep } = useCurrentMembership();

  const [explorerComboEventId, setExplorerComboEventId] = useState<
    string | null
  >(null);
  const [explorerSegment, setExplorerSegment] = useState<string | undefined>();
  const [comboEventStripeDiscountCode, setComboEventStripeDiscountCode] =
    useState("");

  const isExplorerComboRegistration = useMemo(
    () => Boolean(explorerComboEventId?.trim()),
    [explorerComboEventId],
  );

  const inEarlyStageFlow = useMemo(
    () => earlyStagePath || isEarlyStageMembership(formData.membershipName),
    [earlyStagePath, formData.membershipName],
  );

  const showEarlyStageYoungProfessionalLink = useMemo(
    () =>
      !earlyStageStandardTenCommitted &&
      !isEarlyStageStandardTenPoundPathCommitted(
        earlyStageResumeStatus,
        user?.user,
      ),
    [earlyStageStandardTenCommitted, earlyStageResumeStatus, user?.user],
  );

  const earlyStageDirectStandardPath = useMemo(
    () =>
      !earlyStageYoungProfessionalPath ||
      earlyStageStandardTenCommitted ||
      isEarlyStageStandardTenPoundPathCommitted(
        earlyStageResumeStatus,
        user?.user,
      ) ||
      earlyStagePendingProceedingAtStandard(
        earlyStageResumeStatus,
        user?.user,
      ),
    [
      earlyStageYoungProfessionalPath,
      earlyStageStandardTenCommitted,
      earlyStageResumeStatus,
      user?.user,
    ],
  );

  useEffect(() => {
    if (
      isEarlyStageStandardTenPoundPathCommitted(
        earlyStageResumeStatus,
        user?.user,
      )
    ) {
      setEarlyStageStandardTenCommitted(true);
    }
  }, [earlyStageResumeStatus, user?.user]);

  useEffect(() => {
    setExplorerComboEventId(getExplorerPostMembershipEventCombo());
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const { data: comboEventData } = useGetEvent({
    eventId: explorerComboEventId || "",
    enabled: Boolean(explorerComboEventId?.trim()),
  });

  const comboEventEarlyBird = useMemo(
    () =>
      !!comboEventData?.earlyBirdDateEnd &&
      new Date(comboEventData.earlyBirdDateEnd) > new Date(),
    [comboEventData?.earlyBirdDateEnd],
  );

  const eventSubtotal = useMemo(() => {
    if (comboEventData?.price == null) return 0;
    if (comboEventEarlyBird) {
      return Math.max(
        0,
        comboEventData.price - (comboEventData.earlyBirdPrice ?? 0),
      );
    }
    return comboEventData.price;
  }, [comboEventData, comboEventEarlyBird]);

  /** Explorer combo only: Executive = complimentary first event ticket in this signup; Business = 50%. */
  const isExecutiveSelected = useMemo(
    () => formData.membershipName === MEMBERSHIP_EXECUTIVE,
    [formData.membershipName],
  );
  const isBusinessSelected = useMemo(
    () => formData.membershipName === MEMBERSHIP_BUSINESS,
    [formData.membershipName],
  );

  const comboMemberDiscountEligible = eventEligibleForMemberTierDiscount(
    comboEventData?.memberEventDiscountEligible,
  );

  const comboEventCharge = useMemo(() => {
    const freeGroups = comboEventData?.freeForGroups;
    if (isExecutiveSelected && isEventFreeForGroup(freeGroups, "Executive")) {
      return 0;
    }
    if (isBusinessSelected && isEventFreeForGroup(freeGroups, "Business")) {
      return 0;
    }
    if (
      formData.membershipName &&
      (formData.membershipName.toLowerCase().includes("early stage") ||
        formData.membershipName.toLowerCase().includes("youth")) &&
      isEventFreeForGroup(freeGroups, "EarlyStage")
    ) {
      return 0;
    }
    if (!comboMemberDiscountEligible) return eventSubtotal;
    if (isExecutiveSelected) return 0;
    if (isBusinessSelected) return eventSubtotal * 0.5;
    return eventSubtotal;
  }, [
    eventSubtotal,
    isExecutiveSelected,
    isBusinessSelected,
    comboMemberDiscountEligible,
    comboEventData?.freeForGroups,
    formData.membershipName,
  ]);

  const comboEventCatalogPrice = comboEventData?.price ?? 0;
  const comboEventEarlyBirdDiscountAmount = useMemo(
    () =>
      comboEventEarlyBird
        ? Math.max(0, comboEventData?.earlyBirdPrice ?? 0)
        : 0,
    [comboEventEarlyBird, comboEventData?.earlyBirdPrice],
  );

  const comboEventMemberDiscountAmount = useMemo(() => {
    const freeGroups = comboEventData?.freeForGroups;
    const adminFree =
      (isExecutiveSelected && isEventFreeForGroup(freeGroups, "Executive")) ||
      (isBusinessSelected && isEventFreeForGroup(freeGroups, "Business")) ||
      (!!formData.membershipName &&
        (formData.membershipName.toLowerCase().includes("early stage") ||
          formData.membershipName.toLowerCase().includes("youth")) &&
        isEventFreeForGroup(freeGroups, "EarlyStage"));
    // Admin free-for-groups: no membership-discount line (same as complimentary booking style).
    if (adminFree || comboEventCharge <= 0) return 0;
    if (
      comboMemberDiscountEligible &&
      (isExecutiveSelected || isBusinessSelected)
    ) {
      return Math.max(0, eventSubtotal - comboEventCharge);
    }
    return 0;
  }, [
    comboEventData?.freeForGroups,
    isExecutiveSelected,
    isBusinessSelected,
    formData.membershipName,
    comboEventCharge,
    comboMemberDiscountEligible,
    eventSubtotal,
  ]);

  const comboEventMemberDiscountLabel = useMemo(() => {
    const freeGroups = comboEventData?.freeForGroups;
    if (
      (isExecutiveSelected && isEventFreeForGroup(freeGroups, "Executive")) ||
      (isBusinessSelected && isEventFreeForGroup(freeGroups, "Business"))
    ) {
      return null;
    }
    if (isExecutiveSelected) {
      return "Executive Membership — complimentary event ticket (first booking with this signup)";
    }
    if (isBusinessSelected) {
      return "Business Membership — 50% off eligible events (because you are joining this tier)";
    }
    return null;
  }, [
    comboEventData?.freeForGroups,
    isExecutiveSelected,
    isBusinessSelected,
  ]);

  const explorerComboFlow = useMemo(
    () => (explorerComboEventId?.trim() ? getExplorerFlow() : ""),
    [explorerComboEventId],
  );

  const { mutateAsync: bookEventAsync } = useBookEvent();
  const { mutate: sendBookingConfirmationMail } =
    useSendEventBookingConfirmation();

  const stepLabels = useMemo(() => {
    const labels = inEarlyStageFlow
      ? [
          t("membership.step.personalInfo"),
          t("membership.selectMembership"),
          t("membership.step.membershipVerification"),
          t("membership.step.terms"),
          t("membership.step.checkout"),
          t("membership.step.confirmation"),
        ]
      : [
          t("membership.step.personalInfo"),
          t("membership.selectMembership"),
          t("membership.step.terms"),
          t("membership.step.checkout"),
          t("membership.step.confirmation"),
        ];
    return skipPersonalInfoStep ? labels.slice(1) : labels;
  }, [inEarlyStageFlow, skipPersonalInfoStep, t]);

  const progressCurrentStep = skipPersonalInfoStep
    ? Math.max(0, step - 1)
    : step;

  const membershipsList = useMemo(() => {
    const raw = Array.isArray(memberships)
      ? memberships
      : memberships
        ? [memberships]
        : [];
    return sortMembershipsForRegistration(raw);
  }, [memberships]);

  /** Default new signups to Early Stage when no plan is saved yet. */
  useEffect(() => {
    if (membershipsList.length === 0) return;
    if (isExplorerComboRegistration) return;
    const urlMembership = searchParams.get(REGISTER_MEMBERSHIP_QUERY);
    if (
      urlMembership &&
      (ALLOWED_URL_MEMBERSHIPS as readonly string[]).includes(urlMembership)
    ) {
      return;
    }
    setFormData((prev) => {
      if (prev.membershipName?.trim()) return prev;
      const hasEarlyStage = membershipsList.some(
        (m) => m.name === EARLY_STAGE_MEMBERSHIP,
      );
      if (!hasEarlyStage) return prev;
      return { ...prev, membershipName: EARLY_STAGE_MEMBERSHIP };
    });
  }, [membershipsList, searchParams, isExplorerComboRegistration]);

  const { mutate: saveUserMutate, isPending: isSaveUserPending } = useSaveUser({
    onSuccess: async (_data, variables) => {
      if (!formSaveIndicatesPaymentComplete(variables)) {
        return;
      }

      sessionStorage.removeItem("registration_3ds_resume");

      const pendingEv = getExplorerPostMembershipEventCombo();
      const eventPaymentDone =
        sessionStorage.getItem(EXPLORER_EVENT_PAYMENT_DONE_KEY) === "1";
      let bookedEventId: number | null = null;
      if (pendingEv && eventPaymentDone) {
        const n = parseInt(pendingEv, 10);
        if (Number.isFinite(n)) bookedEventId = n;
      }

      if (bookedEventId != null && objectId) {
        try {
          const fd = await resolveEventBookingFormData();
          await bookEventAsync({
            userId: objectId,
            eventId: bookedEventId,
            stageOfBusiness: fd.stageOfBusiness ?? "",
            eventObjectives: fd.objectives ?? "",
            sectorsOfInterest: fd.sectorsOfInterest?.length
              ? fd.sectorsOfInterest.join(", ")
              : "",
            consentToShareProfile: fd.consentToShare ? 1 : 0,
            appliedEventDiscountCode:
              comboEventStripeDiscountCode.trim() || undefined,
          });
          const evCached = queryClient.getQueryData([
            "event",
            explorerComboEventId || "",
          ]) as Event | undefined;
          const stored = localStorage.getItem("UserData");
          const ud = stored ? JSON.parse(stored) : {};
          const toEmail = typeof ud.email === "string" ? ud.email : "";
          const ev = evCached ?? comboEventData;
          if (toEmail.trim() && ev) {
            sendBookingConfirmationMail({
              eventId: bookedEventId,
              email: toEmail,
              name:
                `${ud.firstName || ""} ${ud.lastName || ""}`.trim() || "Member",
              firstName: ud.firstName,
              eventName: ev.name || "SMBF Event",
              eventDateText: ev.eventDate
                ? new Date(ev.eventDate).toLocaleString()
                : "TBC",
            });
          }
        } catch {
          /* paid in Stripe; booking can be retried manually if needed */
        }
        clearExplorerPostMembershipEventCombo();
        sessionStorage.removeItem(EXPLORER_EVENT_PAYMENT_DONE_KEY);
        notifyExplorerComboRegisterChromeChanged();
      }

      const rawMembership = localStorage.getItem("UserData");
      const parsedMembership = rawMembership
        ? (JSON.parse(rawMembership) as { membershipName?: string })
        : {};
      const paidMembershipName =
        typeof parsedMembership.membershipName === "string"
          ? parsedMembership.membershipName
          : formData.membershipName;

      const storedPaidPrice = readRegistrationPaidMembershipPrice();
      if (storedPaidPrice != null && storedPaidPrice > 0) {
        setMembershipPrice(storedPaidPrice);
      }
      clearRegistrationPaidMembershipPrice();

      setStep(isEarlyStageMembership(paidMembershipName) ? 6 : 4);
      if (objectId) {
        setTimeout(() => {
          queryClient.invalidateQueries({ queryKey: ["user", objectId] });
          queryClient.invalidateQueries({ queryKey: ["booked-events"] });
        }, 0);
      }
    },
  });

  useEffect(() => {
    if (!stripe || !objectId) return;
    const params = new URLSearchParams(window.location.search);
    const piSecret = params.get("payment_intent_client_secret");
    const siSecret = params.get("setup_intent_client_secret");
    const redirectStatus = params.get("redirect_status");
    if (redirectStatus !== "succeeded") return;
    if (sessionStorage.getItem("registration_3ds_resume") !== "1") return;
    if (!piSecret && !siSecret) return;

    let cancelled = false;
    void (async () => {
      let ok = false;
      if (piSecret) {
        const { paymentIntent } = await stripe.retrievePaymentIntent(piSecret);
        ok = paymentIntent?.status === "succeeded";
      } else if (siSecret) {
        const { setupIntent } = await stripe.retrieveSetupIntent(siSecret);
        ok = setupIntent?.status === "succeeded";
      }
      if (cancelled || !ok) return;
      sessionStorage.removeItem("registration_3ds_resume");
      window.history.replaceState({}, "", window.location.pathname);

      const raw = localStorage.getItem("UserData");
      if (!raw) return;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const fd = buildRegistrationFormDataAfterPayment({
        effectiveObjectId: objectId,
        userData: parsed,
        discountCode: discountCode || "",
      });
      saveUserMutate(fd);
    })();
    return () => {
      cancelled = true;
    };
  }, [stripe, objectId, discountCode, saveUserMutate]);

  useEffect(() => {
    const storedData = localStorage.getItem("UserData");
    const mergedData = storedData
      ? { ...JSON.parse(storedData), ...userData }
      : userData;
    setUserData(mergedData);
    if (storedData) {
      setUserData(JSON.parse(storedData));
    }
    // Intentionally run once on mount to hydrate from localStorage (merge uses initial userData snapshot).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only hydration
  }, []);

  useEffect(() => {
    const raw = searchParams.get(REGISTER_MEMBERSHIP_QUERY);
    if (!raw) return;
    if ((ALLOWED_URL_MEMBERSHIPS as readonly string[]).includes(raw)) {
      setFormData((prev) => ({ ...prev, membershipName: raw }));
      if (isEarlyStageMembership(raw)) {
        setEarlyStagePath(true);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    const oid = objectId?.trim();
    if (!oid) {
      setRegistrationResuming(false);
      return;
    }

    let cancelled = false;
    setRegistrationResuming(true);

    void (async () => {
      try {
        const res = await axiosInstance.get(
          getAPIURL(`/User/ByObjectId/${encodeURIComponent(oid)}`),
        );
        const u = (res.data?.user ?? res.data?.User) as
          | Record<string, unknown>
          | undefined;
        if (!u || cancelled) return;

        setFormData((prev) => ({
          ...prev,
          firstName:
            (typeof u.firstName === "string" && u.firstName) || prev.firstName,
          lastName:
            (typeof u.lastName === "string" && u.lastName) || prev.lastName,
          email: (typeof u.email === "string" && u.email) || prev.email,
          phoneNumber:
            (typeof u.phoneNumber === "string" && u.phoneNumber) ||
            prev.phoneNumber,
          city: (typeof u.city === "string" && u.city) || prev.city,
          country: (typeof u.country === "string" && u.country) || prev.country,
          personalPositionCompany:
            (typeof u.personalPositionCompany === "string" &&
              u.personalPositionCompany) ||
            prev.personalPositionCompany,
          businessName:
            (typeof u.businessName === "string" && u.businessName) ||
            prev.businessName,
          membershipName:
            (typeof u.membershipName === "string" && u.membershipName) ||
            prev.membershipName,
          isAnnualMembership:
            typeof u.isAnnualMembership === "number"
              ? u.isAnnualMembership
              : prev.isAnnualMembership,
          isTermConditions:
            typeof u.isTermConditions === "number"
              ? u.isTermConditions
              : prev.isTermConditions,
          isDirectory:
            typeof u.isDirectory === "number"
              ? u.isDirectory
              : prev.isDirectory,
        }));

        // Resume steps from DB only — localStorage / form drafts may prefill the
        // Personal Info UI but must not skip that step after signup.
        const snapshot: RegistrationUserSnapshot = {
          firstName: typeof u.firstName === "string" ? u.firstName : "",
          lastName: typeof u.lastName === "string" ? u.lastName : "",
          email: typeof u.email === "string" ? u.email : "",
          phoneNumber: typeof u.phoneNumber === "string" ? u.phoneNumber : "",
          city: typeof u.city === "string" ? u.city : "",
          personalPositionCompany:
            typeof u.personalPositionCompany === "string"
              ? u.personalPositionCompany
              : "",
          businessName: typeof u.businessName === "string" ? u.businessName : "",
          membershipName:
            typeof u.membershipName === "string" ? u.membershipName : "",
          isTermConditions:
            typeof u.isTermConditions === "number" ? u.isTermConditions : 0,
          hasPaid: typeof u.hasPaid === "number" ? u.hasPaid : 0,
          earlyStagePriceTier:
            typeof u.earlyStagePriceTier === "string"
              ? u.earlyStagePriceTier
              : "",
        };
        const isExplorerComboRegistration = Boolean(
          getExplorerPostMembershipEventCombo(),
        );

        if (isExplorerComboRegistration) {
          try {
            const me = await getMyExplorerOnboarding();
            const ex = me.explorer as
              | Record<string, unknown>
              | null
              | undefined;
            const seg =
              (typeof me.payload?.segment === "string" &&
                me.payload.segment.trim()) ||
              (typeof ex?.attendeeType === "string" &&
                ex.attendeeType.trim()) ||
              (typeof ex?.AttendeeType === "string" &&
                ex.AttendeeType.trim()) ||
              "";
            if (seg) setExplorerSegment(seg);
          } catch {
            /* explorer profile optional */
          }
        }

        let status: EarlyStageVerificationStatus | null = null;
        try {
          status = await getEarlyStageStatus();
        } catch {
          status = null;
        }

        const urlMembership =
          searchParams.get(REGISTER_MEMBERSHIP_QUERY)?.trim() ?? "";
        // Do not use formData default Early Stage — that + localStorage personal
        // draft incorrectly skipped Personal Info before membership.
        const effectiveMembership =
          snapshot.membershipName?.trim() || urlMembership || "";

        const early =
          isEarlyStageMembership(effectiveMembership) ||
          isEarlyStageVerificationInProgress(status);

        setEarlyStagePath(early);

        if (cancelled) return;

        if (userHasNavigatedRef.current) {
          if (early) {
            setEarlyStageResumeStatus(status);
          }
          return;
        }

        if (early) {
          setEarlyStageResumeStatus(status);
          const verificationStatus = (status?.status ?? "").trim();
          const tier = (status?.priceTier ?? snapshot.earlyStagePriceTier ?? "").trim();
          const youngProPath =
            (verificationStatus === "Pending" ||
              verificationStatus === "UnderReview" ||
              needsEarlyStagePricePlanSelection(status) ||
              (verificationStatus === "Rejected" && tier !== "Premium10")) &&
            !earlyStagePendingProceedingAtStandard(status, snapshot);
          setEarlyStageYoungProfessionalPath(youngProPath);
          const resumeObjectId =
            (typeof u.objectId === "string" && u.objectId.trim()) ||
            (typeof u.ObjectId === "string" && u.ObjectId.trim()) ||
            objectId?.trim() ||
            "";
          if (isEarlyStageRejectionNoticeVisible(status, resumeObjectId)) {
            setStep(2);
          } else {
            // £2 YP Pending/UnderReview resumes at age step — never force Personal Info.
            setStep(resolveEarlyStageRegistrationStep(snapshot, status));
          }
        } else {
          // Unpaid explorers already completed profile at signup — always open on
          // Select Membership. Signup event-terms must not skip membership terms/checkout.
          if (skipPersonalInfoStep && !isPaidPortalMember(snapshot)) {
            setFormData((prev) => ({
              ...prev,
              isTermConditions: 0,
            }));
            setStep(1);
            return;
          }
          // Explorer combo signup should always start from Personal Info so users
          // explicitly complete Personal + Membership steps after first login.
          if (isExplorerComboRegistration && !isPaidPortalMember(snapshot)) {
            setStep(0);
            return;
          }
          // Explorer (non-combo): unpaid users stay on membership until terms are
          // accepted so a refresh does not lock in the first clicked tier in checkout.
          const isExplorerNonComboRegistration =
            !isExplorerComboRegistration &&
            typeof sessionStorage !== "undefined" &&
            sessionStorage.getItem("explorer_flow") != null;
          if (
            isExplorerNonComboRegistration &&
            !isPaidPortalMember(snapshot) &&
            hasRegistrationPersonalInfoSaved(snapshot) &&
            snapshot.isTermConditions !== 1
          ) {
            setStep(1);
            return;
          }
          setStep(resolveStandardRegistrationStep(snapshot));
        }
      } catch {
        /* keep current step on resume failure */
      } finally {
        if (!cancelled) setRegistrationResuming(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [objectId, searchParams, skipPersonalInfoStep]);

  useEffect(() => {
    if (!skipPersonalInfoStep || registrationResuming) return;
    if (step === 0) setStep(1);
  }, [skipPersonalInfoStep, registrationResuming, step]);

  useEffect(() => {
    if (!inEarlyStageFlow || !objectId?.trim()) return;
    if (step < 2) return;
    void getEarlyStageStatus()
      .then(setEarlyStageResumeStatus)
      .catch(() => {});
  }, [inEarlyStageFlow, objectId, step]);

  useEffect(() => {
    const pendingLike =
      earlyStageResumeStatus?.status === "Pending" ||
      earlyStageResumeStatus?.status === "UnderReview";
    if (!inEarlyStageFlow || !pendingLike) return;
    if (earlyStagePendingProceedingAtStandard(earlyStageResumeStatus)) return;
    if (step > 2) setStep(2);
  }, [inEarlyStageFlow, earlyStageResumeStatus, step]);

  const continueAtStandardWhilePending = useCallback(async () => {
    const membershipName = isEarlyStageMembership(formData.membershipName)
      ? formData.membershipName.trim()
      : EARLY_STAGE_MEMBERSHIP;
    setFormData((prev) => ({ ...prev, membershipName }));
    setMembershipPrice(10);
    const status = await continueEarlyStageAtStandardRate();
    setEarlyStageResumeStatus(status);
    setEarlyStageStandardTenCommitted(true);
    const termsDone =
      (user?.user?.isTermConditions ?? formData.isTermConditions) === 1;
    setStep(termsDone ? 5 : 4);
    if (objectId) {
      queryClient.invalidateQueries({ queryKey: ["user", objectId] });
    }
  }, [
    formData.membershipName,
    formData.isTermConditions,
    objectId,
    queryClient,
    user?.user?.isTermConditions,
  ]);

  useEffect(() => {
    if (!inEarlyStageFlow || step !== 3) return;
    if (
      earlyStageResumeStatus?.status === "Approved" &&
      !needsEarlyStagePricePlanSelection(earlyStageResumeStatus)
    ) {
      setStep(4);
    }
  }, [inEarlyStageFlow, step, earlyStageResumeStatus]);

  useEffect(() => {
    if (!inEarlyStageFlow || step !== 5) return;
    if (needsEarlyStagePricePlanSelection(earlyStageResumeStatus)) {
      setStep(2);
    }
  }, [inEarlyStageFlow, step, earlyStageResumeStatus]);

  const nextStep = () => {
    userHasNavigatedRef.current = true;
    setStep((s) => s + 1);
  };
  const prevStep = () => {
    userHasNavigatedRef.current = true;
    setStep((s) => {
      if (skipPersonalInfoStep && s <= 1) return 1;
      return Math.max(s - 1, 0);
    });
  };

  const leaveMembershipWizard = useCallback(() => {
    userHasNavigatedRef.current = true;
    navigate(-1);
  }, [navigate]);

  const earlyStagePrevStep = useCallback(() => {
    userHasNavigatedRef.current = true;
    setStep((s) => {
      if (s === 4 && !earlyStageYoungProfessionalPath) return 1;
      if (s === 4) return 2;
      return Math.max(s - 1, 0);
    });
  }, [earlyStageYoungProfessionalPath]);

  const advanceAfterAgeVerification = useCallback(
    (status: EarlyStageVerificationStatus) => {
      userHasNavigatedRef.current = true;
      setEarlyStageResumeStatus(status);
      if (
        status.requiresAdminApproval ||
        status.status === "Pending" ||
        status.status === "UnderReview"
      ) {
        return;
      }
      if (status.status === "Rejected") {
        const rejectObjectId =
          objectId?.trim() || user?.user?.objectId?.trim() || "";
        if (isEarlyStageRejectionNoticeVisible(status, rejectObjectId)) {
          setStep(2);
          return;
        }
        const termsDone =
          (user?.user?.isTermConditions ?? formData.isTermConditions) === 1;
        setStep(termsDone ? 5 : 4);
        return;
      }
      if (needsEarlyStagePricePlanSelection(status)) {
        setStep(2);
        return;
      }
      if (status.status === "Approved") {
        setStep(4);
        return;
      }
      nextStep();
    },
    [formData.isTermConditions, user?.user?.isTermConditions],
  );

  const continueAfterRejectionNotice = useCallback(() => {
    void getEarlyStageStatus()
      .then((status) => {
        setEarlyStageResumeStatus(status);
        const termsDone =
          (user?.user?.isTermConditions ?? formData.isTermConditions) === 1;
        setStep(termsDone ? 5 : 4);
      })
      .catch(() => setStep(4));
  }, [formData.isTermConditions, user?.user?.isTermConditions]);

  const continueAfterApproval = useCallback(
    (monthlyFeeGbp?: number) => {
      userHasNavigatedRef.current = true;
      if (monthlyFeeGbp != null) {
        setMembershipPrice(monthlyFeeGbp);
      }
      setStep(4);
    },
    [],
  );

  const persistPersonalInfo = useCallback(
    (data: PersonalInfoData) => {
      // Keep wizard data local until payment succeeds — Users row is updated post-pay.
      setFormData((prev) => ({
        ...prev,
        firstName: data.firstName ?? prev.firstName,
        lastName: data.lastName ?? prev.lastName,
        email: data.email ?? prev.email,
        phoneNumber: data.phoneNumber ?? prev.phoneNumber,
        city: data.city ?? prev.city,
        country: data.country ?? prev.country,
        personalPositionCompany:
          data.personalPositionCompany ?? prev.personalPositionCompany,
        businessName: data.businessName ?? prev.businessName,
        referralCodeUsed: data.referralCodeUsed ?? prev.referralCodeUsed,
      }));
      return Promise.resolve();
    },
    [],
  );

  const handlePersonalInfoNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  const handleEarlyStageYoungProfessional = useCallback(
    (selectedFromForm?: string) => {
      userHasNavigatedRef.current = true;
      setEarlyStageYoungProfessionalPath(true);
      setEarlyStageStandardTenCommitted(false);
      setEarlyStagePath(true);
      const membershipName =
        selectedFromForm && isEarlyStageMembership(selectedFromForm)
          ? selectedFromForm.trim()
          : EARLY_STAGE_MEMBERSHIP;
      setMembershipPrice(0);
      setFormData((prev) => ({ ...prev, membershipName }));
      setStep(2);
    },
    [],
  );

  const advanceEarlyStageStandardDirect = useCallback(
    async (data: Partial<FormData>, price: number) => {
      userHasNavigatedRef.current = true;
      setEarlyStageYoungProfessionalPath(false);
      setEarlyStagePath(true);
      setFormData((prev) => ({ ...prev, ...data }));
      const fee = price > 0 ? price : 10;
      setMembershipPrice(fee);
      // Do not call submit-verification / User/Update here — that previously
      // auto-set earlyStageVerificationStatus=Approved before payment.
      setEarlyStageResumeStatus({
        status: "NotSubmitted",
        ageTier: "TwentyThreePlus",
        priceTier: "Premium10",
        canPay: true,
        requiresAdminApproval: false,
        canSelectPriceTier: false,
        resubmissionCount: 0,
      });
      setEarlyStageStandardTenCommitted(true);
      setStep(4);
    },
    [],
  );

  const handleMembershipNext = (data: Partial<FormData>, price: number) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setMembershipPrice(price);

    const membershipName = data.membershipName ?? formData.membershipName;
    const isEarly = isEarlyStageMembership(membershipName);

    if (!isEarly) {
      userHasNavigatedRef.current = true;
      setEarlyStagePath(false);
      nextStep();
      return;
    }

    setEarlyStagePath(true);
    void advanceEarlyStageStandardDirect(data, price);
  };

  const handleTermsNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    if (earlyStagePath) {
      void (async () => {
        try {
          const status = await getEarlyStageStatus();
          setEarlyStageResumeStatus(status);
          if (needsEarlyStagePricePlanSelection(status)) {
            setStep(2);
            return;
          }
        } catch {
          /* proceed to checkout if status unavailable */
        }
        setStep(5);
      })();
      return;
    }
    nextStep();
  };

  useEffect(() => {
    localStorage.setItem(
      "UserData",
      JSON.stringify({
        ...formData,
        objectId: objectId || "",
        discountCode: discountCode,
      }),
    );
  }, [formData, objectId, discountCode]);

  /** Resume checkout with membership saved in DB but in-memory price still 0. */
  useEffect(() => {
    if (membershipPrice > 0) return;
    const name = formData.membershipName?.trim();
    if (!name || membershipsList.length === 0) return;
    const row = findMembershipForCheckout(membershipsList, name);
    if (!row) return;
    const raw =
      formData.isAnnualMembership === 1 ? row.annualFee : row.monthlyFee;
    const fee = Number(raw);
    if (Number.isFinite(fee) && fee > 0) {
      setMembershipPrice(fee);
    }
  }, [
    formData.membershipName,
    formData.isAnnualMembership,
    membershipsList,
    membershipPrice,
  ]);

  const handleCheckoutNext = () => {
    nextStep();
  };

  const getAccessToken = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) return;

      setFormData((prev) => ({
        ...prev,
        // AuthUsers flow: keep these empty until user fills, or prefill if saved in storage
        email: localStorage.getItem("auth_email") || prev.email,
        isGDPRConsent: 0,
        isEmailUpdates: 0,
      }));
    } catch (err) {
      console.error("Token retrieval failed", err);
    }
  };

  useEffect(() => {
    getAccessToken();
  }, []);

  // Personal Info step: prefill from portal profile, then Explorer lead, then saved UserData / auth email (empty fields only)
  useEffect(() => {
    let cancelled = false;
    if (!localStorage.getItem("access_token")) return;

    const fromPortal = (): Partial<FormData> => {
      const u = user?.user;
      if (!u) return {};
      const { city, country } = splitCityCountry(
        u.city?.trim() ?? "",
        u.country?.trim() ?? "",
      );
      return {
        firstName: u.firstName?.trim() ?? "",
        lastName: u.lastName?.trim() ?? "",
        email: u.email?.trim() ?? "",
        phoneNumber: u.phoneNumber?.trim() ?? "",
        city,
        country,
        personalPositionCompany: u.personalPositionCompany?.trim() ?? "",
        businessName: u.businessName?.trim() ?? "",
      };
    };

    const fromAuthEmail = (): Partial<FormData> => {
      const e = localStorage.getItem("auth_email")?.trim();
      return e ? { email: e } : {};
    };

    const run = async () => {
      // Explorer session/API before portal so fresh Step-1 country (e.g. Brazil)
      // is not blocked by a stale User.Country (e.g. Pakistan).
      const layers: Partial<FormData>[] = [explorerSessionToFormPartial()];
      try {
        const me = await getMyExplorerOnboarding();
        if (cancelled) return;
        layers.push(explorerToFormPartial(me.explorer));
      } catch {
        /* explorer unavailable */
      }
      layers.push(fromPortal());
      layers.push(readUserDataFromStorage());
      layers.push(fromAuthEmail());

      setFormData((prev) => mergeEmptyPersonalFields(prev, layers));
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const renderStep = () => {
    if (inEarlyStageFlow) {
      switch (step) {
        case 0:
          return (
            <PersonalInfoStep
              defaultValues={{
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: formData.phoneNumber,
                city: formData.city,
                country: formData.country,
                personalPositionCompany: formData.personalPositionCompany,
                businessName: formData.businessName,
              }}
              onNext={handlePersonalInfoNext}
              onAutoSave={persistPersonalInfo}
            />
          );
        case 1:
          return (
            <MembershipStep
              discountCode={discountCode}
              setDiscountCode={setDiscountCode}
              previewUserId={objectId?.trim() || undefined}
              previewEmail={formData.email}
              previewName={`${formData.firstName} ${formData.lastName}`.trim()}
              defaultValues={{
                membershipName: formData.membershipName,
                isAnnualMembership: formData.isAnnualMembership,
              }}
              memberships={membershipsList}
              setMembershipPrice={setMembershipPrice}
              onNext={handleMembershipNext}
              onBack={skipPersonalInfoStep ? leaveMembershipWizard : prevStep}
              onEarlyStageYoungProfessional={handleEarlyStageYoungProfessional}
              showEarlyStageYoungProfessionalLink={
                showEarlyStageYoungProfessionalLink
              }
            />
          );
        case 2:
          if (
            isEarlyStageRejectionNoticeVisible(
              earlyStageResumeStatus,
              objectId?.trim() || user?.user?.objectId?.trim() || "",
            )
          ) {
            return (
              <EarlyStageRejectedNotice
                rejectedAt={earlyStageResumeStatus?.rejectedAt}
                onContinue={continueAfterRejectionNotice}
              />
            );
          }
          if (
            (earlyStageResumeStatus?.status === "Pending" ||
              earlyStageResumeStatus?.status === "UnderReview") &&
            !earlyStagePendingProceedingAtStandard(earlyStageResumeStatus)
          ) {
            return (
              <EarlyStagePendingStep
                onContinueAtStandardRate={continueAtStandardWhilePending}
              />
            );
          }
          if (earlyStageResumeStatus?.status === "Approved") {
            return (
              <EarlyStageApprovedStep
                status={earlyStageResumeStatus}
                onContinue={continueAfterApproval}
                onStatusUpdated={setEarlyStageResumeStatus}
              />
            );
          }
          if (
            earlyStageResumeStatus?.status === "Rejected" &&
            isEarlyStageRejectionNoticeVisible(
              earlyStageResumeStatus,
              objectId?.trim() || user?.user?.objectId?.trim() || "",
            )
          ) {
            return (
              <EarlyStageRejectedNotice
                rejectedAt={earlyStageResumeStatus.rejectedAt}
                onContinue={continueAfterRejectionNotice}
              />
            );
          }
          return (
            <RegistrationEarlyStageAgeStep
              prefillFromPersonalInfo={{
                phoneNumber: formData.phoneNumber,
                city: formData.city,
                country: formData.country,
              }}
              membershipName={formData.membershipName}
              membershipId={resolveMembershipIdForName(
                membershipsList,
                formData.membershipName,
              )}
              isAnnualMembership={formData.isAnnualMembership}
              explorerSegment={explorerSegment}
              onAdvanceAfterVerification={advanceAfterAgeVerification}
              onBack={prevStep}
              onStatusUpdated={setEarlyStageResumeStatus}
            />
          );
        case 3:
          return (
            <EarlyStagePlanStep
              onBack={prevStep}
              onComplete={(fee) => {
                setMembershipPrice(fee);
                void getEarlyStageStatus()
                  .then((status) => {
                    setEarlyStageResumeStatus(status);
                    setStep(4);
                  })
                  .catch(() => {
                    setStep(4);
                  });
              }}
            />
          );
        case 4:
          return (
            <TermsStep
              defaultValues={{
                isTermConditions: formData.isTermConditions,
                isDirectory: formData.isDirectory,
              }}
              onNext={handleTermsNext}
              onBack={earlyStagePrevStep}
            />
          );
        case 5:
          return (
            <CheckoutStep
              selectedMembership={formData.membershipName}
              membershipPrice={membershipPrice}
              memberships={membershipsList}
              isAnnualMembership={
                formData.isAnnualMembership === 1 ? "annual" : "monthly"
              }
              onNext={handleCheckoutNext}
              onBack={prevStep}
              hideEarlyStagePriceChoice
              earlyStageDirectStandardPath={earlyStageDirectStandardPath}
              skipPrePaymentMembershipSync
              discountCode={discountCode}
              explorerComboEventId={explorerComboEventId}
              comboEventName={comboEventData?.name ?? ""}
              comboEventPrice={comboEventCharge}
              comboEventEarlyBird={comboEventEarlyBird}
              comboEventCatalogPrice={comboEventCatalogPrice}
              comboEventEarlyBirdDiscountAmount={
                comboEventEarlyBirdDiscountAmount
              }
              comboEventMemberDiscountLabel={comboEventMemberDiscountLabel}
              comboEventMemberDiscountAmount={comboEventMemberDiscountAmount}
              comboEventCurrency={comboEventData?.currency ?? "gbp"}
              comboEventProductId={comboEventData?.productId ?? ""}
              explorerComboFlow={explorerComboFlow}
              showExplorerEventStripePromo={Boolean(
                explorerComboEventId?.trim() &&
                !isExplorerComboRegistration &&
                (comboEventCharge > 0 || comboEventMemberDiscountAmount > 0),
              )}
              comboEventStripeDiscountCode={comboEventStripeDiscountCode}
              onComboEventStripeDiscountCodeChange={
                setComboEventStripeDiscountCode
              }
              saveUserMutate={saveUserMutate}
              isSaveUserPending={isSaveUserPending}
            />
          );
        case 6:
          return (
            <ConfirmationStep
              membershipPrice={membershipPrice}
              discountCode={discountCode}
              selectedMembership={formData.membershipName}
              isAnnualMembership={
                formData.isAnnualMembership === 1 ? "annual" : "monthly"
              }
            />
          );
        default:
          return null;
      }
    }

    switch (step) {
      case 0:
        return (
          <PersonalInfoStep
            defaultValues={{
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phoneNumber: formData.phoneNumber,
              city: formData.city,
              country: formData.country,
              personalPositionCompany: formData.personalPositionCompany,
              businessName: formData.businessName,
            }}
            onNext={handlePersonalInfoNext}
            onAutoSave={persistPersonalInfo}
          />
        );
      case 1:
        return (
          <MembershipStep
            discountCode={discountCode}
            setDiscountCode={setDiscountCode}
            previewUserId={objectId?.trim() || undefined}
            previewEmail={formData.email}
            previewName={`${formData.firstName} ${formData.lastName}`.trim()}
            defaultValues={{
              membershipName: formData.membershipName,
              isAnnualMembership: formData.isAnnualMembership,
            }}
            memberships={membershipsList}
            setMembershipPrice={setMembershipPrice}
            onNext={handleMembershipNext}
            onBack={skipPersonalInfoStep ? leaveMembershipWizard : prevStep}
            onEarlyStageYoungProfessional={handleEarlyStageYoungProfessional}
            showEarlyStageYoungProfessionalLink={
              showEarlyStageYoungProfessionalLink
            }
          />
        );
      case 2:
        return (
          <TermsStep
            defaultValues={{
              isTermConditions: formData.isTermConditions,
              isDirectory: formData.isDirectory,
            }}
            onNext={handleTermsNext}
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <CheckoutStep
            selectedMembership={formData.membershipName}
            membershipPrice={membershipPrice}
            memberships={membershipsList}
            isAnnualMembership={
              formData.isAnnualMembership === 1 ? "annual" : "monthly"
            }
            onNext={handleCheckoutNext}
            onBack={prevStep}
            discountCode={discountCode}
            skipPrePaymentMembershipSync
            explorerComboEventId={explorerComboEventId}
            comboEventName={comboEventData?.name ?? ""}
            comboEventPrice={comboEventCharge}
            comboEventEarlyBird={comboEventEarlyBird}
            comboEventCatalogPrice={comboEventCatalogPrice}
            comboEventEarlyBirdDiscountAmount={
              comboEventEarlyBirdDiscountAmount
            }
            comboEventMemberDiscountLabel={comboEventMemberDiscountLabel}
            comboEventMemberDiscountAmount={comboEventMemberDiscountAmount}
            comboEventCurrency={comboEventData?.currency ?? "gbp"}
            comboEventProductId={comboEventData?.productId ?? ""}
            explorerComboFlow={explorerComboFlow}
            showExplorerEventStripePromo={Boolean(
              explorerComboEventId?.trim() &&
              !isExplorerComboRegistration &&
              (comboEventCharge > 0 || comboEventMemberDiscountAmount > 0),
            )}
            comboEventStripeDiscountCode={comboEventStripeDiscountCode}
            onComboEventStripeDiscountCodeChange={
              setComboEventStripeDiscountCode
            }
            saveUserMutate={saveUserMutate}
            isSaveUserPending={isSaveUserPending}
          />
        );
      case 4:
        return (
          <ConfirmationStep
            membershipPrice={membershipPrice}
            discountCode={discountCode}
            selectedMembership={formData.membershipName}
            isAnnualMembership={
              formData.isAnnualMembership === 1 ? "annual" : "monthly"
            }
          />
        );
      default:
        return null;
    }
  };

  if (registrationResuming) {
    return (
      <div
        className="m-4 p-4 rounded-4 border-0 shadow d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: 220 }}
      >
        <Spinner animation="border" variant="primary" role="status" />
        <p className="text-muted mt-3 mb-0 small">Loading your registration…</p>
      </div>
    );
  }

  return (
    <div className="m-4 p-4 rounded-4 border-0 shadow d-flex flex-column">
      <StepsProgressBar currentStep={progressCurrentStep} labels={stepLabels} />
      {inEarlyStageFlow &&
        step >= 3 &&
        earlyStageResumeStatus?.status !== "Pending" &&
        earlyStageResumeStatus?.status !== "UnderReview" && (
          <EarlyStageRegistrationNotice
            status={earlyStageResumeStatus}
            step={step}
            directStandardPath={earlyStageDirectStandardPath}
          />
        )}
      {renderStep()}
    </div>
  );
};

export default RegisterUser;
